<script setup lang="ts">
import { transformerRenderIndentGuides } from '@shikijs/transformers'
import { useElementBounding } from '@vueuse/core'
import type { getShikiInstance } from '@/utils/shikiHighlighter'
import { getShikiHighlighter } from '@/utils/shikiHighlighter'
import DropdownVars from './DropdownVars.vue'
import { convertBlocksToText, convertTextToBlocks, isInVarList } from './utils'
import type { CustomVars, I18nStrings } from './interfaces'

type ParseError = { message: string; offset: number | null } | null

const INDENT = '  '
const LANG_OPTIONS: { value: 'json' | 'xml'; label: string }[] = [
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' }
]
const VAR_REGEX = /(\{\{\s*[\w.]+(?:\([^)]*\))?\s*\}\})/g
const HAS_VAR_REGEX = /\{\{\s*[\w.]+(?:\([^)]*\))?\s*\}\}/
const VAR_TRANSFORMER = {
  postprocess: (output: string) => output.replace(VAR_REGEX, '<span class="shiki-var">$1</span>')
}

const props = withDefaults(
  defineProps<{
    modelValue: string
    lang: 'json' | 'xml'
    customVars?: CustomVars[]
    i18nStrings?: I18nStrings
    label?: string
    placeholder?: string
    isDisabled?: boolean
    dataCy?: string | null
    forceError?: boolean
  }>(),
  {
    customVars: () => [],
    i18nStrings: undefined,
    label: undefined,
    placeholder: undefined,
    isDisabled: false,
    dataCy: null,
    forceError: false
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:lang': [value: 'json' | 'xml']
  'update:exampleValue': [value: string]
  hasError: [value: boolean]
}>()

const { t } = useI18n()

const editorRef = ref<HTMLDivElement | null>(null)
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const highlightedHtml = ref('')
const isEditing = ref(false)
const savedSelection = ref<{ start: number; end: number } | null>(null)
const syntaxErrorMessage = ref<string | null>(null)

const { width: editorWidth, left: editorLeft } = useElementBounding(editorRef)

const indentGuidesTransformer = transformerRenderIndentGuides()

function extractJsonErrorOffset(msg: string, code: string): number {
  const v8Match = msg.match(/at position (\d+)/)
  if (v8Match) return parseInt(v8Match[1], 10)

  const ffMatch = msg.match(/line (\d+) column (\d+)/)
  if (ffMatch) {
    const lineIdx = parseInt(ffMatch[1], 10) - 1
    const col = parseInt(ffMatch[2], 10) - 1
    return (
      code
        .split('\n')
        .slice(0, lineIdx)
        .reduce((acc, l) => acc + l.length + 1, 0) + col
    )
  }
  return 0
}

function parseSyntaxError(code: string, lang: 'json' | 'xml'): ParseError {
  if (!code.trim()) return null

  // Replace {{ vars }} with the literal `null` so the JSON/XML structure can still be validated
  const hasVars = HAS_VAR_REGEX.test(code)
  const sanitized = hasVars ? code.replace(VAR_REGEX, 'null') : code

  if (lang === 'json') {
    try {
      JSON.parse(sanitized)
      return null
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'JSON invalide'
      // When vars are present the offsets in `sanitized` differ from `code`, so skip highlighting
      return { message: msg, offset: hasVars ? null : extractJsonErrorOffset(msg, code) }
    }
  }

  // XML: vars inside attributes can confuse the parser even after substitution — skip offset
  const doc = new DOMParser().parseFromString(sanitized, 'application/xml')
  const errEl = doc.querySelector('parsererror')
  return errEl ? { message: 'XML invalide', offset: null } : null
}

function offsetToPosition(code: string, offset: number) {
  const before = code.slice(0, Math.min(offset, code.length))
  const lines = before.split('\n')
  return { line: lines.length - 1, character: lines[lines.length - 1].length }
}

function applyHighlight(hl: ReturnType<typeof getShikiInstance>, code: string) {
  if (!hl) return

  const parseError = parseSyntaxError(code, props.lang)
  syntaxErrorMessage.value = parseError?.message ?? null

  const decorations =
    parseError?.offset != null
      ? [
          {
            start: offsetToPosition(code, parseError.offset),
            end: offsetToPosition(code, Math.min(parseError.offset + 4, code.length)),
            properties: { class: 'shiki-syntax-error' }
          }
        ]
      : []

  const html = hl.codeToHtml(code || ' ', {
    lang: props.lang,
    theme: 'github-light',
    transformers: [VAR_TRANSFORMER, indentGuidesTransformer],
    decorations
  })
  const innerMatch = html.match(/<pre[^>]*>([\s\S]*)<\/pre>/)
  highlightedHtml.value = innerMatch?.[1] ?? ''
}

function autoResize() {
  const textarea = textareaRef.value
  if (!textarea) return
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

function onInput(e: Event) {
  emit('update:modelValue', (e.target as HTMLTextAreaElement).value)
  autoResize()
}

function onTab(e: KeyboardEvent) {
  const textarea = e.target as HTMLTextAreaElement
  const start = textarea.selectionStart
  const newValue = props.modelValue.slice(0, start) + INDENT + props.modelValue.slice(textarea.selectionEnd)
  emit('update:modelValue', newValue)
  nextTick(() => textarea.setSelectionRange(start + INDENT.length, start + INDENT.length))
}

function onEnter(e: KeyboardEvent) {
  const textarea = e.target as HTMLTextAreaElement
  const start = textarea.selectionStart
  const lineStart = props.modelValue.lastIndexOf('\n', start - 1) + 1
  const indent = props.modelValue.slice(lineStart, start).match(/^(\s*)/)?.[1] ?? ''
  const insertion = '\n' + indent
  const newValue = props.modelValue.slice(0, start) + insertion + props.modelValue.slice(textarea.selectionEnd)
  emit('update:modelValue', newValue)
  nextTick(() => textarea.setSelectionRange(start + insertion.length, start + insertion.length))
}

function startEditing() {
  if (props.isDisabled) return
  isEditing.value = true
  nextTick(() => {
    textareaRef.value?.focus()
    autoResize()
  })
}

async function stopEditing() {
  if (textareaRef.value) {
    savedSelection.value = {
      start: textareaRef.value.selectionStart,
      end: textareaRef.value.selectionEnd
    }
  }
  isEditing.value = false
  applyHighlight(await getShikiHighlighter(), props.modelValue)
}

function insertVar(code: string) {
  const insertion = ` {{ ${code} }} `
  const sel = isEditing.value
    ? {
        start: textareaRef.value?.selectionStart ?? props.modelValue.length,
        end: textareaRef.value?.selectionEnd ?? props.modelValue.length
      }
    : (savedSelection.value ?? { start: props.modelValue.length, end: props.modelValue.length })

  const newValue = props.modelValue.slice(0, sel.start) + insertion + props.modelValue.slice(sel.end)
  emit('update:modelValue', newValue)

  isEditing.value = true
  nextTick(() => {
    const pos = sel.start + insertion.length
    textareaRef.value?.focus()
    textareaRef.value?.setSelectionRange(pos, pos)
  })
}

const resolvedI18n = computed<I18nStrings>(() => {
  if (props.i18nStrings) return props.i18nStrings
  return {
    textareaLabel: t('global.words.text'),
    characters: t('global.words.characters'),
    insertVar: t('global.customVars.insertLabel'),
    errorEmojiInMessage: '',
    errorUnknownVar: t('ui.error.strings.unknownVar'),
    customValue: {
      placeholder: t('global.customVars.customValue.placeholder'),
      create: t('global.customVars.customValue.add'),
      update: t('global.customVars.customValue.modify'),
      back: t('global.customVars.customValue.back')
    }
  }
})

const formattedBlocks = computed(() => convertTextToBlocks(props.modelValue))

const errorUnknownVar = computed(() =>
  formattedBlocks.value.some((b) => b.isVar && !isInVarList({ varCode: b.textValue, customVars: props.customVars }))
)

const hasError = computed(() => errorUnknownVar.value || !!syntaxErrorMessage.value || props.forceError)

function refreshPreviewText() {
  emit('update:exampleValue', convertBlocksToText(formattedBlocks.value, true, props.customVars))
}

onMounted(async () => {
  applyHighlight(await getShikiHighlighter(), props.modelValue)
  emit('update:exampleValue', convertBlocksToText(formattedBlocks.value, true, props.customVars))
})

watch(
  () => props.modelValue,
  async (newVal) => {
    if (!isEditing.value) {
      applyHighlight(await getShikiHighlighter(), newVal)
    }
    emit('update:exampleValue', convertBlocksToText(formattedBlocks.value, true, props.customVars))
  }
)

watch(
  () => props.lang,
  async () => applyHighlight(await getShikiHighlighter(), props.modelValue)
)

watch(hasError, (val) => emit('hasError', val), { immediate: true })

defineExpose({ refreshPreviewText })
</script>

<template>
  <div class="sm:col-span-6">
    <label v-if="label" class="block text-sm text-foreground">{{ label }}</label>

    <ul v-if="syntaxErrorMessage || errorUnknownVar" class="mt-1 flex flex-col gap-0.5 list-disc list-inside text-xs">
      <li v-if="syntaxErrorMessage" class="text-red-600">
        {{ syntaxErrorMessage }}
      </li>
      <li v-if="errorUnknownVar" class="text-red-600">
        {{ resolvedI18n.errorUnknownVar }}
      </li>
    </ul>

    <div
      v-show="!isEditing"
      ref="editorRef"
      :class="[
        'shiki-display mt-2 min-h-[78px] max-h-[500px] overflow-y-auto w-full rounded-md border px-3 py-2 font-mono sm:text-sm',
        isDisabled
          ? 'cursor-default border-input bg-muted text-foreground-disabled'
          : 'cursor-text border-input bg-white text-foreground',
        { '!border-red-300': hasError }
      ]"
      style="tab-size: 2; -moz-tab-size: 2"
      @click="startEditing"
      v-html="highlightedHtml"
    />
    <p
      v-if="!isEditing && !modelValue && placeholder"
      class="pointer-events-none absolute mt-2 px-3 py-2 text-sm text-muted-foreground"
    >
      {{ placeholder }}
    </p>

    <textarea
      v-show="isEditing"
      ref="textareaRef"
      class="mt-2 block min-h-[78px] w-full resize-none rounded-control border border-primary px-3 py-2 font-mono !ring-0 sm:text-sm"
      :class="{ '!border-red-300': hasError }"
      style="tab-size: 2; -moz-tab-size: 2; white-space: pre-wrap; max-height: 500px; overflow-y: auto"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :data-cy="dataCy"
      @input="onInput"
      @keydown.tab.prevent="onTab"
      @keydown.enter.prevent="onEnter"
      @blur="stopEditing"
    />

    <div class="mt-1 flex flex-row items-center gap-2">
      <Select
        :model-value="lang"
        :disabled="isDisabled"
        @update:model-value="emit('update:lang', $event as 'json' | 'xml')"
      >
        <SelectTrigger
          class="h-7 w-auto gap-1 border-0 px-2 font-mono text-xs text-muted-foreground shadow-none hover:bg-accent focus:ring-0"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="opt in LANG_OPTIONS" :key="opt.value" :value="opt.value" class="font-mono text-xs">
            {{ opt.label }}
          </SelectItem>
        </SelectContent>
      </Select>

      <div class="flex-1" />

      <div v-if="customVars.length && !isDisabled" class="relative">
        <DropdownVars
          :label="resolvedI18n.insertVar"
          :custom-vars="customVars"
          :i18n-strings="resolvedI18n"
          :textarea-position="{ width: editorWidth, left: editorLeft }"
          @var-clicked="insertVar"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "@/input.css";

.shiki-display :deep(code) {
  white-space: pre-wrap;
  word-break: break-all;
  display: block;
}

.shiki-display :deep(.shiki-var) {
  @apply rounded bg-primary/10 px-0.5 text-primary;
}

.shiki-display :deep(.shiki-syntax-error) {
  @apply rounded bg-red-100 text-red-700 underline decoration-red-400 decoration-wavy;
}

.shiki-display :deep(.indent-guide) {
  display: inline-block;
  border-left: 1px solid hsl(var(--border));
}
</style>
